import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowUpDown, Download, MapPinOff, Search, X } from "lucide-react";
import { Avatar } from "@/components/Avatar";
import { Permission, useCan } from "@/components/session-provider";
import {
  exportUsersWithoutSite,
  fetchUsersWithoutSite,
  MinimumJustificationLength,
  NoEmailToken,
  type SortDirection,
  type UnsitedDomain,
  type UnsitedPerson,
  type UnsitedPersonTeam,
  type UnsitedQuery,
  type UnsitedSortField,
} from "@/lib/reports-users-without-site";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";

export const Route = createFileRoute("/reports/users-without-site")({
  component: UsersWithoutSitePage,
});

/**
 * **Users without a site.**
 *
 * D-50 places somebody its matching rule does not match at NO site rather than at a guess, and
 * leaves a person matched by two sites at neither. That is the safe behaviour and its cost is a
 * population nothing has ever listed: an unplaced person still raises requests, still appears in
 * the "No site" row of a breakdown (D-54), and nothing until now could open that row and say who
 * they were.
 *
 * ⚠️ **Gated on `user.manage`, not on a reporting tier**, and the server is what enforces that.
 * The rows here are roster rows — names, emails, reporting lines — so the roster's fence is the
 * one that applies. Gating a directory listing on `report.self` would hand every agent a paged
 * staff directory, past every limit D-51 put on the requester picker for exactly that reason.
 *
 * ⚠️ `useCan` below decides whether the "open profile" affordance is DRAWN. It is not the
 * control: `/api/people/{id}` re-checks, and this report and that profile share one permission,
 * so in practice a reader who can open this page can always follow its links. The check is here
 * so the two cannot drift apart silently if either gate is ever changed.
 */
function UsersWithoutSitePage() {
  const canOpenProfiles = useCan(Permission.UserManage);

  const [query, setQuery] = useState<UnsitedQuery>({
    sortBy: "Name",
    direction: "Ascending",
  });

  /**
   * What is typed, as distinct from what has been asked for.
   *
   * ⚠️ **The search runs on SUBMIT, never per keystroke** — D-33 and D-78's rule. Every load of
   * this report writes an audit event, so a live search files one for `p`, `pr` and `pri` on the
   * way to `printer`, and burns the search rate limit doing it. A debounce narrows that window;
   * it does not close it.
   */
  const [typed, setTyped] = useState("");

  const [rows, setRows] = useState<UnsitedPerson[]>([]);
  const [totalMatching, setTotalMatching] = useState<number | null>(null);
  const [totalUnsited, setTotalUnsited] = useState<number | null>(null);
  const [totalPeople, setTotalPeople] = useState<number | null>(null);
  const [domains, setDomains] = useState<UnsitedDomain[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Every filter is part of the fetch key. Serialised rather than listed so adding one cannot be
  // forgotten here and silently stop refetching.
  const key = useMemo(() => JSON.stringify(query), [query]);

  useEffect(() => {
    const controller = new AbortController();

    setLoading(true);
    setError(null);

    fetchUsersWithoutSite(query)
      .then((page) => {
        if (controller.signal.aborted) return;

        setRows(page.items);
        setTotalMatching(page.totalMatching);
        setTotalUnsited(page.totalUnsited);
        setTotalPeople(page.totalPeople);
        setDomains(page.domains);
        setCursor(page.nextCursor);
      })
      .catch((cause) => {
        if (controller.signal.aborted) return;
        setError(cause instanceof Error ? cause.message : "That report could not be loaded.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const loadMore = useCallback(() => {
    if (!cursor || loadingMore) return;

    setLoadingMore(true);

    fetchUsersWithoutSite(query, cursor)
      .then((page) => {
        setRows((existing) => [...existing, ...page.items]);
        setCursor(page.nextCursor);
      })
      .catch(() => setError("The next page could not be loaded."))
      .finally(() => setLoadingMore(false));
  }, [cursor, query, loadingMore]);

  /**
   * ⚠️ Clicking the sorted column flips the direction; clicking another starts it ascending.
   * The cursor is not carried over — the server discards one made under a different sort, because
   * it describes a position in an ordering that no longer applies.
   */
  const sortBy = (field: UnsitedSortField) => {
    setQuery((q) => ({
      ...q,
      sortBy: field,
      direction: q.sortBy === field && q.direction === "Ascending" ? "Descending" : "Ascending",
    }));
  };

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setQuery((q) => ({ ...q, search: typed.trim() || undefined }));
  };

  const filtered =
    query.search !== undefined || query.domain !== undefined || query.hasManager !== undefined;

  const clearFilters = () => {
    setTyped("");
    setQuery((q) => ({
      sortBy: q.sortBy,
      direction: q.direction,
      includeInactive: q.includeInactive,
    }));
  };

  const runExport = async (justification: string) => {
    setExporting(true);
    setError(null);

    try {
      await exportUsersWithoutSite(query, justification);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "That export could not be produced.");
      throw cause;
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 p-4 sm:p-6">
      <header className="space-y-3">
        <Link
          to="/reports"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          Reports
        </Link>

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">Users without a site</h1>
            <p className="mt-0.5 max-w-2xl text-sm text-muted-foreground">
              {/*
                ⚠️ States the CAUSE, not just the count. Somebody reading this needs to know that
                an unplaced person is what the rules are supposed to do when nothing matches —
                otherwise the obvious reading is that the sync is broken, and the obvious response
                is to widen a matching rule until the list empties, which is how one site quietly
                acquires everybody.
              */}
              A site is matched on one directory field, and somebody the rule does not match is
              placed at no site rather than at a guess. Two sites that both match one person leave
              them at neither. These are the people that left unplaced — it is what the rules are
              meant to do when nothing matches, not a sync failure.
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={query.includeInactive ?? false}
                onChange={(event) =>
                  setQuery((q) => ({ ...q, includeInactive: event.target.checked || undefined }))
                }
              />
              Include deactivated
            </label>

            <ExportButton
              disabled={loading || (totalMatching ?? 0) === 0}
              busy={exporting}
              rowCount={totalMatching ?? 0}
              filtered={filtered}
              onExport={runExport}
            />
          </div>
        </div>

        <Counts
          matching={totalMatching}
          unsited={totalUnsited}
          people={totalPeople}
          filtered={filtered}
          includeInactive={query.includeInactive ?? false}
        />

        <DomainSummary
          domains={domains}
          total={totalUnsited}
          selected={query.domain}
          onSelect={(domain) =>
            setQuery((q) => ({ ...q, domain: q.domain === domain ? undefined : domain }))
          }
        />
      </header>

      <section className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card p-3">
        <form onSubmit={submitSearch} className="flex min-w-[16rem] flex-1 items-center gap-2">
          <div className="relative flex-1">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <input
              type="search"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              placeholder="Search name, email or job title"
              aria-label="Search name, email or job title"
              className="h-9 w-full rounded-lg border border-border bg-background pl-8 pr-3 text-sm focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:outline-none"
            />
          </div>

          {/*
            ⚠️ A real submit button, not just Enter. The search runs on submit rather than per
            keystroke, and a box that only responds to a key nobody was told to press reads as
            broken — people type, wait, and conclude the search does not work.
          */}
          <button
            type="submit"
            className="h-9 shrink-0 rounded-lg border border-border px-3 text-sm font-medium hover:bg-muted"
          >
            Search
          </button>
        </form>

        <Field label="Email domain">
          <select
            value={query.domain ?? ""}
            onChange={(event) =>
              setQuery((q) => ({ ...q, domain: event.target.value || undefined }))
            }
            className="h-9 rounded-lg border border-border bg-background px-2 text-sm"
          >
            <option value="">Any domain</option>
            {/*
              ⚠️ The no-email bucket comes from the FACET now, with its count, rather than being a
              hardcoded option here. It was hardcoded and countless, which made it the one filter
              on the screen you had to select to discover whether it matched anybody.
            */}
            {domains.map((domain) => (
              <option key={domain.domain} value={domain.domain}>
                {domainLabel(domain.domain)} ({domain.count})
              </option>
            ))}
          </select>
        </Field>

        <Field label="Manager">
          <select
            value={query.hasManager === undefined ? "" : String(query.hasManager)}
            onChange={(event) =>
              setQuery((q) => ({
                ...q,
                hasManager: event.target.value === "" ? undefined : event.target.value === "true",
              }))
            }
            className="h-9 rounded-lg border border-border bg-background px-2 text-sm"
          >
            <option value="">Anyone</option>
            <option value="true">Has a manager</option>
            {/* The actionable one: nobody to ask about them. */}
            <option value="false">No manager</option>
          </select>
        </Field>

        {filtered && (
          <button
            type="button"
            onClick={clearFilters}
            className="flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
            Clear filters
          </button>
        )}
      </section>

      {error && (
        <p className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm">
          {error}
        </p>
      )}

      {loading ? (
        <p className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          Loading…
        </p>
      ) : rows.length === 0 ? (
        <Empty filtered={filtered} includeInactive={query.includeInactive ?? false} />
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full min-w-[48rem] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <SortableHeader
                    label="Name"
                    field="Name"
                    active={query.sortBy}
                    direction={query.direction}
                    onSort={sortBy}
                    className="pl-4"
                  />
                  <SortableHeader
                    label="Email"
                    field="Email"
                    active={query.sortBy}
                    direction={query.direction}
                    onSort={sortBy}
                  />
                  <SortableHeader
                    label="Job title"
                    field="JobTitle"
                    active={query.sortBy}
                    direction={query.direction}
                    onSort={sortBy}
                  />
                  <th scope="col" className="py-2.5 pr-3 font-medium">
                    Manager
                  </th>
                  <th scope="col" className="py-2.5 pr-4 font-medium">
                    Role
                  </th>
                </tr>
              </thead>

              <tbody>
                {rows.map((person) => (
                  <PersonRow key={person.id} person={person} linked={canOpenProfiles} />
                ))}
              </tbody>
            </table>
          </div>

          {cursor && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-60"
              >
                {loadingMore ? "Loading…" : "Show more"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * The figures, with their denominators.
 *
 * ⚠️ D-53's rule: 38 unplaced people is alarming out of 40 and unremarkable out of 2,600. And
 * D-58's: a filter must narrow AND SAY SO — without the unfiltered total beside it, a reader who
 * has typed a search sees a small number and no way to tell whether the problem is small or
 * whether they are looking at a slice of it.
 */
function Counts({
  matching,
  unsited,
  people,
  filtered,
  includeInactive,
}: {
  matching: number | null;
  unsited: number | null;
  people: number | null;
  filtered: boolean;
  includeInactive: boolean;
}) {
  if (matching === null || unsited === null || people === null) return null;

  const accounts = includeInactive ? "accounts" : "active accounts";

  return (
    <p className="text-sm">
      <span className="text-lg font-semibold">{matching.toLocaleString()}</span>
      <span className="text-muted-foreground">
        {filtered ? (
          <>
            {" "}
            matching, of {unsited.toLocaleString()} with no site, of {people.toLocaleString()}{" "}
            {accounts}
          </>
        ) : (
          <>
            {" "}
            of {people.toLocaleString()} {accounts} have no site
          </>
        )}
      </span>
    </p>
  );
}

/** The no-email bucket travels as a token; everything else is the domain itself. */
function domainLabel(domain: string): string {
  return domain === NoEmailToken ? "No email address" : domain;
}

/**
 * Where the unplaced people's email addresses come from, at a glance.
 *
 * ⚠️ **This is the question behind the whole report.** An unplaced population that is all one
 * domain is a missing site rule and a five-minute fix; one scattered across many is a directory
 * problem. The dropdown below already held these counts, but a reader had to open it and read
 * down a list to see the shape — which is the one thing worth seeing immediately.
 *
 * ⚠️ **It reconciles** (D-54). Every bucket sums to the unplaced total, including the people with
 * no email address at all, and the strip says so. A breakdown that does not add up teaches the
 * reader to distrust the figure above it — and this one silently omitted the no-email bucket
 * until the server started carrying it.
 *
 * ⚠️ **Folds, never truncates** (D-54). A long tail collapses into a single "N more" carrying the
 * remainder, so the sum still holds. That row is deliberately NOT a button: there is no filter
 * that means "the other domains", and a control that looks clickable and does nothing is worse
 * than plain text. The full list stays in the dropdown.
 */
function DomainSummary({
  domains,
  total,
  selected,
  onSelect,
}: {
  domains: readonly UnsitedDomain[];
  total: number | null;
  selected: string | undefined;
  onSelect: (domain: string) => void;
}) {
  if (domains.length === 0 || total === null) return null;

  const shown = domains.slice(0, MaxDomainChips);
  const rest = domains.slice(MaxDomainChips);
  const remainder = rest.reduce((sum, domain) => sum + domain.count, 0);

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs text-muted-foreground">By email domain:</span>

      {shown.map((domain) => {
        const active = selected === domain.domain;

        return (
          <button
            key={domain.domain}
            type="button"
            onClick={() => onSelect(domain.domain)}
            aria-pressed={active}
            className={cn(
              "inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs transition-colors",
              active ? "border-ring bg-secondary font-medium" : "border-border hover:bg-muted",
            )}
          >
            {domainLabel(domain.domain)}
            <span className={cn(active ? "" : "text-muted-foreground")}>{domain.count}</span>
          </button>
        );
      })}

      {rest.length > 0 && (
        <span className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-1.5 py-0.5 text-xs text-muted-foreground">
          {rest.length} more
          <span>{remainder}</span>
        </span>
      )}

      <span className="text-xs text-muted-foreground">
        {/* Says the sum out loud, so the reconciliation is checkable rather than merely true. */}={" "}
        {total.toLocaleString()} with no site
      </span>
    </div>
  );
}

/**
 * ⚠️ Small enough to stay one line on a laptop. Past this the strip stops being a glance and
 * becomes a list, which is what the dropdown is for.
 */
const MaxDomainChips = 6;

function Empty({ filtered, includeInactive }: { filtered: boolean; includeInactive: boolean }) {
  return (
    <div className="rounded-xl border border-border bg-card p-10 text-center">
      <MapPinOff className="mx-auto h-6 w-6 text-muted-foreground" aria-hidden />

      {filtered ? (
        <>
          <p className="mt-3 text-sm font-medium">Nothing matches these filters.</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {/*
              ⚠️ Names the filters as the reason. The first version of this screen said "everybody
              has a site" whatever the state, which is a claim about the organisation made on the
              strength of somebody's search box.
            */}
            There are still people with no site — clear the filters to see them.
          </p>
        </>
      ) : (
        <>
          <p className="mt-3 text-sm font-medium">Everybody has a site.</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Every {includeInactive ? "account" : "active account"} matches exactly one site rule.
          </p>
        </>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

/**
 * ⚠️ The sort is a real button inside the header cell, with `aria-sort` on the cell. A `<th>` with
 * an onClick is invisible to a keyboard and unannounced to a screen reader, which on a table
 * whose whole point is reordering makes the feature unreachable rather than merely awkward.
 */
function SortableHeader({
  label,
  field,
  active,
  direction,
  onSort,
  className,
}: {
  label: string;
  field: UnsitedSortField;
  active: UnsitedSortField | undefined;
  direction: SortDirection | undefined;
  onSort: (field: UnsitedSortField) => void;
  className?: string;
}) {
  const isActive = active === field;
  const ascending = direction === "Ascending";

  return (
    <th
      scope="col"
      className={cn("py-2.5 pr-3 font-medium", className)}
      aria-sort={isActive ? (ascending ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        onClick={() => onSort(field)}
        className="flex items-center gap-1 hover:text-foreground focus-visible:underline focus-visible:outline-none"
      >
        {label}
        {isActive ? (
          <span aria-hidden className="text-foreground">
            {ascending ? "▲" : "▼"}
          </span>
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-40" aria-hidden />
        )}
      </button>
    </th>
  );
}

/**
 * ⚠️ The NAME is the link, not a chevron at the end of the row. A whole-row link would make the
 * manager cell — which is its own link to a different person — a nested anchor, which is invalid
 * and behaves differently in every browser.
 */
function PersonRow({ person, linked }: { person: UnsitedPerson; linked: boolean }) {
  return (
    <tr className="border-b border-border last:border-0 hover:bg-muted/40">
      <td className="py-2.5 pl-4 pr-3">
        <div className="flex items-center gap-2.5">
          <Avatar
            userId={person.id}
            name={person.displayName}
            hasPicture={person.hasAvatar}
            size="sm"
          />

          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <PersonLink id={person.id} name={person.displayName} linked={linked} />

            {!person.isActive && <Tag tone="muted">Deactivated</Tag>}

            {/*
              ⚠️ Badged rather than filtered out. A guest having no site is expected — the
              matching rules read directory attributes a guest has none of — so hiding them
              would be honest about the question and dishonest about the total.
            */}
            {person.isExternalGuest && <Tag tone="muted">Guest</Tag>}
          </div>
        </div>
      </td>

      <td className="py-2.5 pr-3 text-muted-foreground">
        {person.email ?? <span className="text-muted-foreground/60">No email</span>}
      </td>

      <td className="py-2.5 pr-3 text-muted-foreground">
        {person.jobTitle ?? <span className="text-muted-foreground/60">—</span>}
      </td>

      <td className="py-2.5 pr-3">
        {person.managerName ? (
          person.managerUserId ? (
            <PersonLink id={person.managerUserId} name={person.managerName} linked={linked} />
          ) : (
            person.managerName
          )
        ) : (
          // ⚠️ Says which fact is missing. "—" here would read as "no manager recorded" and as
          // "the manager has no name" identically, and only the first is actionable.
          <span className="text-muted-foreground/60">No manager</span>
        )}
      </td>

      <td className="py-2.5 pr-4">
        <Roles person={person} />
      </td>
    </tr>
  );
}

function PersonLink({ id, name, linked }: { id: string; name: string; linked: boolean }) {
  if (!linked) return <span className="font-medium">{name}</span>;

  return (
    <Link
      to="/people/$id"
      params={{ id }}
      className="font-medium underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none"
    >
      {name}
    </Link>
  );
}

/**
 * What this person is, across the whole system and on each team.
 *
 * ⚠️ **Both, never just the global list.** The roster shipped with global roles alone and it
 * produced a real misreading: somebody whose administrative role was held on a team rendered as
 * "Requester", and the administrator who had just demoted them read that as the demotion having
 * worked. A team-held role is drawn as `Role · Team` so the cause of an administrative capability
 * is visible rather than merely its consequence.
 */
function Roles({ person }: { person: UnsitedPerson }) {
  if (person.roles.length === 0 && person.teams.length === 0) {
    return <span className="text-muted-foreground/60">—</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {person.roles.map((role) => (
        <Tag key={role}>{role}</Tag>
      ))}

      {person.teams.map((team) => (
        <TeamRole key={team.teamId} team={team} />
      ))}
    </div>
  );
}

function TeamRole({ team }: { team: UnsitedPersonTeam }) {
  return (
    <Tag tone={team.isAdministrative ? "administrative" : "default"}>
      {team.roleName}
      <span className="text-muted-foreground"> · {team.teamName}</span>
    </Tag>
  );
}

function Tag({
  children,
  tone = "default",
}: {
  children: React.ReactNode;
  tone?: "default" | "muted" | "administrative";
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md border px-1.5 py-0.5 text-xs",
        tone === "administrative"
          ? "border-amber-500/40 bg-amber-500/10"
          : tone === "muted"
            ? "border-border bg-muted text-muted-foreground"
            : "border-border bg-secondary",
      )}
    >
      {children}
    </span>
  );
}

/**
 * Exporting the report.
 *
 * ⚠️ **The justification is collected before anything leaves**, and it is not a formality: what
 * downloads is a spreadsheet of named colleagues with their email addresses and reporting lines,
 * and it leaves the audited system permanently. The server refuses a short one, records it at
 * High severity, caps the rows and writes a watermark naming the requester into the file itself.
 *
 * ⚠️ The dialog SAYS all of that. A control whose consequences are only in the audit ledger
 * teaches people that the ledger is decoration.
 */
function ExportButton({
  disabled,
  busy,
  rowCount,
  filtered,
  onExport,
}: {
  disabled: boolean;
  busy: boolean;
  rowCount: number;
  filtered: boolean;
  onExport: (justification: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [justification, setJustification] = useState("");

  const tooShort = justification.trim().length < MinimumJustificationLength;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (tooShort) return;

    try {
      await onExport(justification.trim());
      setOpen(false);
      setJustification("");
    } catch {
      // Reported on the page behind the dialog, which stays open so the typed reason is not lost.
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className="flex h-9 items-center gap-1.5 rounded-lg border border-border px-3 text-sm font-medium hover:bg-muted disabled:opacity-50"
      >
        <Download className="h-3.5 w-3.5" aria-hidden />
        Export CSV
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <form
            onSubmit={submit}
            className="w-full max-w-md space-y-4 rounded-xl border border-border bg-card p-5 shadow-lg"
          >
            <div>
              <h2 className="font-semibold">Export {rowCount.toLocaleString()} people</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {filtered
                  ? "The current filters are applied, and are recorded inside the file."
                  : "Every unplaced person in the current view."}
              </p>
            </div>

            {/*
              ⚠️ Says what the file contains and what happens when it is produced. "Personal data"
              rather than "PHI", because that is what this is — and overstating it would teach
              people to discount the warning on exports that really do carry clinical detail.
            */}
            <p className="rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
              The file contains names, email addresses and reporting lines. It is recorded against
              your account as a high-severity export and carries a watermark identifying you.
            </p>

            <label className="block space-y-1">
              <span className="text-sm font-medium">Why is this export needed?</span>
              <textarea
                value={justification}
                onChange={(event) => setJustification(event.target.value)}
                rows={3}
                autoFocus
                className="w-full rounded-lg border border-border bg-background p-2 text-sm focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:outline-none"
                placeholder="e.g. Assigning sites for the Q3 directory clean-up"
              />
              <span className="text-xs text-muted-foreground">
                At least {MinimumJustificationLength} characters.
              </span>
            </label>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-2 text-sm font-medium hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={tooShort || busy}
                className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                {busy ? "Preparing…" : "Export"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
